import { User, Heart, TrendingUp, Activity, Brain, Sparkles, CheckCircle2, FileText, Table2, Clock3 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const SHARE_BLOCKS = [
  { key: 'patient_data', label: 'Dados do Paciente', icon: User },
  { key: 'health_score', label: 'Score de Saúde', icon: Heart },
  { key: 'indicators', label: 'Indicadores de Saúde', icon: TrendingUp },
  { key: 'analytical_report', label: 'Relatório Analítico', icon: Table2 },
  { key: 'timeline', label: 'Timeline', icon: Clock3 },
  { key: 'insights', label: 'Insights e Alertas', icon: Activity },
  { key: 'health_map', label: 'Mapa de Sistemas', icon: Brain },
  { key: 'analysis', label: 'Análise Clínica', icon: Sparkles },
  { key: 'recommendations', label: 'Recomendações', icon: FileText },
  { key: 'highlights', label: 'Destaques Clínicos', icon: CheckCircle2 },
] as const;

export type ShareBlockKey = typeof SHARE_BLOCKS[number]['key'];

export interface ShareBlocksState {
  patient_data: boolean;
  health_score: boolean;
  indicators: boolean;
  analytical_report: boolean;
  timeline: boolean;
  insights: boolean;
  health_map: boolean;
  analysis: boolean;
  recommendations: boolean;
  highlights: boolean;
}

export const DEFAULT_SHARE_BLOCKS: ShareBlocksState = {
  patient_data: true,
  health_score: true,
  indicators: true,
  analytical_report: true,
  timeline: true,
  insights: true,
  health_map: true,
  analysis: true,
  recommendations: true,
  highlights: true,
};

interface ShareBlockSelectorProps {
  selectedBlocks: ShareBlocksState;
  onChange: (blocks: ShareBlocksState) => void;
}

export function ShareBlockSelector({ selectedBlocks, onChange }: ShareBlockSelectorProps) {
  const handleToggle = (key: ShareBlockKey) => {
    onChange({
      ...selectedBlocks,
      [key]: !selectedBlocks[key],
    });
  };

  const selectAll = () => {
    onChange(DEFAULT_SHARE_BLOCKS);
  };

  const clearAll = () => {
    onChange({
      patient_data: false,
      health_score: false,
      indicators: false,
      analytical_report: false,
      timeline: false,
      insights: false,
      health_map: false,
      analysis: false,
      recommendations: false,
      highlights: false,
    });
  };

  const selectedCount = Object.values(selectedBlocks).filter(Boolean).length;

  return (
    <div className="space-y-4">
      {/* Header com contagem e ações */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {selectedCount} de {SHARE_BLOCKS.length} seções selecionadas
        </span>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={selectAll} className="text-xs h-7 px-2">
            Selecionar Todos
          </Button>
          <Button variant="ghost" size="sm" onClick={clearAll} className="text-xs h-7 px-2">
            Limpar
          </Button>
        </div>
      </div>

      {/* Lista de blocos */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {SHARE_BLOCKS.map(({ key, label, icon: Icon }) => (
          <div
            key={key}
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              selectedBlocks[key]
                ? 'bg-primary/5 border-primary/30'
                : 'bg-muted/30 border-border/50 hover:bg-muted/50'
            }`}
            onClick={() => handleToggle(key)}
          >
            <Checkbox
              id={key}
              checked={selectedBlocks[key]}
              onCheckedChange={() => handleToggle(key)}
              className="pointer-events-none"
            />
            <Icon className={`h-4 w-4 ${selectedBlocks[key] ? 'text-primary' : 'text-muted-foreground'}`} />
            <Label
              htmlFor={key}
              className={`text-sm cursor-pointer ${selectedBlocks[key] ? 'font-medium' : 'text-muted-foreground'}`}
            >
              {label}
            </Label>
          </div>
        ))}
      </div>
    </div>
  );
}
